<template>
  <div class="modal-overlay" @click.self="$emit('close')">

    <div v-if="loading" class="qr-modal-loading">
      <AppPageLoader>{{ $t('general.loading') }}</AppPageLoader>
    </div>

    <div v-else-if="error" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <p>{{ error }}</p>
      <BaseButton @click="tryAgain">
        {{ $t('general.tryAgain') }}
      </BaseButton>
    </div>

    <div v-else-if="singleMode && qrCode" class="qr-floating-card">
      <button @click="$emit('close')" class="floating-close">
        <font-awesome-icon icon="fa-times" />
      </button>

      <div class="qr-card-content">
        <h3 class="qr-title">{{ qrCode.table_name }}</h3>

        <div class="qr-image-container">
          <img :src="qrCode.qr_code_url" :alt="$t('tables.qrCodeFor') + ' ' + qrCode.table_name" class="qr-image" />
        </div>

        <div class="qr-details">
          <p class="location-name">{{ $t('tables.locationName') }}: {{ qrCode.location_name }}</p>
          <div class="qr-hint">Skeniraj za narudžbu</div>
        </div>

        <div class="qr-card-actions">
          <BaseButton icon="fa-download" @click="downloadQR(qrCode)">
            {{ $t('tables.downloadQR') }}
          </BaseButton>
          <BaseButton variant="secondary" icon="fa-print" @click="printQR">
            {{ $t('tables.printQR') }}
          </BaseButton>
        </div>
      </div>
    </div>

    <div v-else-if="!singleMode" class="qr-batch-modal">
      <button @click="$emit('close')" class="floating-close">
        <font-awesome-icon icon="fa-times" />
      </button>

      <div class="qr-batch-content">
        <div v-if="!qrCodes.length" class="batch-form">
          <h2 class="batch-title">{{ $t('tables.generateQRBatch') }}</h2>

          <div class="batch-option-container">
            <div class="batch-option-tabs">
              <div class="batch-option-tab" :class="{ active: batchMode === 'all' }" @click="batchMode = 'all'">
                <font-awesome-icon icon="fa-table" class="tab-icon" />
                {{ $t('tables.allTables') }}
              </div>
              <div class="batch-option-tab" :class="{ active: batchMode === 'selected' }"
                @click="batchMode = 'selected'">
                <font-awesome-icon icon="fa-check-square" class="tab-icon" />
                {{ $t('tables.selectTables') }}
              </div>
            </div>

            <div v-if="batchMode === 'selected'" class="tables-selection-container">
              <h3 class="selection-title">{{ $t('tables.selectTablesToGenerate') }}</h3>

              <div class="selection-actions" v-if="availableTables.length > 0">
                <button @click="selectAllTables" type="button" class="select-action">
                  <font-awesome-icon icon="fa-check-double" />
                  {{ $t('tables.selectAll') }}
                </button>
                <button @click="deselectAllTables" type="button" class="select-action">
                  <font-awesome-icon icon="fa-times" />
                  {{ $t('tables.deselectAll') }}
                </button>
              </div>

              <div class="tables-selection">
                <div v-if="availableTables.length === 0" class="no-tables">
                  <font-awesome-icon icon="fa-exclamation-circle" class="no-tables-icon" />
                  {{ $t('tables.noTablesAvailable') }}
                </div>
                <div v-else class="tables-grid">
                  <div v-for="table in availableTables" :key="table.uuid" class="table-selection-item"
                    :class="{ selected: selectedTableIds.includes(table.uuid) }"
                    @click="toggleTableSelection(table.uuid)">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="selectedTableIds" :value="table.uuid" class="checkbox-input" />
                      <span class="table-name">{{ table.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <BaseButton type="button" variant="secondary" icon="fa-times" @click="$emit('close')">
              {{ $t('general.cancel') }}
            </BaseButton>
            <BaseButton
              icon="fa-qrcode"
              :disabled="!canGenerate"
              :loading="loading"
              @click="generateBatchQR"
            >
              {{ $t('tables.generateQRCodes') }}
            </BaseButton>
          </div>
        </div>

        <div v-else class="qr-batch-result">
          <div class="qr-batch-header">
            <h3 class="result-title">
              <font-awesome-icon icon="fa-check-circle" class="success-icon" />
              {{ qrCodes.length }} {{ $t('tables.qrCodesGenerated') }}
            </h3>
            <div class="batch-actions">
              <BaseButton icon="fa-download" @click="downloadAllQR">
                {{ $t('tables.downloadAllQR') }}
              </BaseButton>
              <BaseButton variant="secondary" icon="fa-print" @click="printAllQR">
                {{ $t('tables.printAllQR') }}
              </BaseButton>
            </div>
          </div>

          <div class="qr-grid">
            <div v-for="qr in qrCodes" :key="qr.table_uuid" class="qr-card">
              <div class="qr-card-inner">
                <div class="qr-card-header">
                  <h4>{{ qr.table_name }}</h4>
                </div>
                <div class="qr-card-image">
                  <img :src="qr.qr_code_url" :alt="$t('tables.qrCodeFor') + ' ' + qr.table_name" />
                </div>
                <div class="qr-card-footer">
                  <p class="qr-location">{{ qr.location_name }}</p>
                  <button @click="downloadQR(qr)" class="btn-download" title="Preuzmi">
                    <font-awesome-icon icon="fa-download" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import BaseButton from '@/components/shared/BaseButton.vue';
import { tableApi } from '@/services/api';
import { Table, QRCodeResponse } from '@/types/tables';
import { useI18n } from 'vue-i18n';
import JSZip from 'jszip';

const props = defineProps({
  table: {
    type: Object as () => Table | null,
    default: null
  },
  locationId: {
    type: String,
    required: true
  },
  singleMode: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const loading = ref(false);
const error = ref<string | null>(null);
const qrCode = ref<QRCodeResponse | null>(null);
const qrCodes = ref<QRCodeResponse[]>([]);
const availableTables = ref<Table[]>([]);

const batchMode = ref('all');
const selectedTableIds = ref<string[]>([]);

const canGenerate = computed(() => {
  if (batchMode.value === 'all') return true;
  return selectedTableIds.value.length > 0;
});

const fetchTables = async () => {
  try {
    availableTables.value = await tableApi.getTablesForLocation(props.locationId);
  } catch (err: any) {
    error.value = err.message || t('tables.errorFetchingTables');
  }
};

const selectAllTables = () => {
  selectedTableIds.value = availableTables.value.map(table => table.uuid);
};

const deselectAllTables = () => {
  selectedTableIds.value = [];
};

const toggleTableSelection = (uuid: string) => {
  const index = selectedTableIds.value.indexOf(uuid);
  if (index === -1) {
    selectedTableIds.value.push(uuid);
  } else {
    selectedTableIds.value.splice(index, 1);
  }
};

const generateSingleQR = async () => {
  if (!props.table) return;

  loading.value = true;
  error.value = null;

  try {
    qrCode.value = await tableApi.generateQRCode(props.table.uuid);
  } catch (err: any) {
    error.value = err.message || t('tables.errorGeneratingQR');
  } finally {
    loading.value = false;
  }
};

const generateBatchQR = async () => {
  loading.value = true;
  error.value = null;

  try {
    const request = {
      all_tables: batchMode.value === 'all',
      table_uuids: batchMode.value === 'selected' ? selectedTableIds.value : undefined
    };

    qrCodes.value = await tableApi.generateBatchQRCodes(props.locationId, request);
  } catch (err: any) {
    error.value = err.message || t('tables.errorGeneratingQRCodes');
  } finally {
    loading.value = false;
  }
};

const downloadQR = (qrCode: QRCodeResponse) => {

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {

    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.onload = () => {

    const qrSize = Math.max(img.width, img.height);
    canvas.width = qrSize + 80;
    canvas.height = qrSize + 150;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#E9ECEF';
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

    const titleGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    titleGradient.addColorStop(0, '#505050');
    titleGradient.addColorStop(1, '#D8670C');

    ctx.fillStyle = titleGradient;
    ctx.textAlign = 'center';
    ctx.font = 'bold 20px Arial';
    ctx.fillText(qrCode.table_name, canvas.width / 2, 40);

    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(
      (canvas.width - qrSize) / 2 - 10,
      60 - 10,
      qrSize + 20,
      qrSize + 20
    );

    ctx.shadowColor = 'transparent';

    ctx.drawImage(
      img,
      (canvas.width - qrSize) / 2,
      60,
      qrSize,
      qrSize
    );

    ctx.strokeStyle = '#E9ECEF';
    ctx.lineWidth = 1;
    ctx.strokeRect(
      (canvas.width - qrSize) / 2 - 5,
      60 - 5,
      qrSize + 10,
      qrSize + 10
    );

    ctx.fillStyle = '#505050';
    ctx.font = '16px Arial';
    ctx.fillText(qrCode.location_name, canvas.width / 2, qrSize + 85);

    ctx.font = '12px Arial';
    ctx.fillStyle = '#888888';
    ctx.fillText('Skeniraj za narudžbu', canvas.width / 2, qrSize + 115);

    const dataUrl = canvas.toDataURL('image/png');

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `qr-${qrCode.table_name.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  img.src = qrCode.qr_code_url;
};

const downloadAllQR = async () => {
  if (qrCodes.value.length === 0) return;

  loading.value = true;

  try {

    const zip = new JSZip();

    let loadedImages = 0;
    const totalImages = qrCodes.value.length;

    const finalizeZip = async () => {
      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `qr-codes-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
      loading.value = false;
    };

    const processQRCode = (qrCode: QRCodeResponse) => {
      return new Promise<void>((resolve) => {

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {

          loadedImages++;
          resolve();
          return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {

          const qrSize = Math.max(img.width, img.height);
          canvas.width = qrSize + 80;
          canvas.height = qrSize + 150;

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.strokeStyle = '#E9ECEF';
          ctx.lineWidth = 2;
          ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

          const titleGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
          titleGradient.addColorStop(0, '#505050');
          titleGradient.addColorStop(1, '#D8670C');

          ctx.fillStyle = titleGradient;
          ctx.textAlign = 'center';
          ctx.font = 'bold 20px Arial';
          ctx.fillText(qrCode.table_name, canvas.width / 2, 40);

          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(
            (canvas.width - qrSize) / 2 - 10,
            60 - 10,
            qrSize + 20,
            qrSize + 20
          );

          ctx.shadowColor = 'transparent';

          ctx.drawImage(
            img,
            (canvas.width - qrSize) / 2,
            60,
            qrSize,
            qrSize
          );

          ctx.strokeStyle = '#E9ECEF';
          ctx.lineWidth = 1;
          ctx.strokeRect(
            (canvas.width - qrSize) / 2 - 5,
            60 - 5,
            qrSize + 10,
            qrSize + 10
          );

          ctx.fillStyle = '#505050';
          ctx.font = '16px Arial';
          ctx.fillText(qrCode.location_name, canvas.width / 2, qrSize + 85);

          ctx.font = '12px Arial';
          ctx.fillStyle = '#888888';
          ctx.fillText('Skeniraj za narudžbu', canvas.width / 2, qrSize + 115);

          canvas.toBlob(blob => {
            if (blob) {
              const fileName = `qr-${qrCode.table_name.replace(/\s+/g, '-').toLowerCase()}.png`;
              zip.file(fileName, blob);
            }

            loadedImages++;
            if (loadedImages === totalImages) {
              finalizeZip();
            }
            resolve();
          }, 'image/png');
        };

        img.onerror = () => {

          loadedImages++;
          if (loadedImages === totalImages) {
            finalizeZip();
          }
          resolve();
        };

        img.src = qrCode.qr_code_url;
      });
    };

    const promises = qrCodes.value.map(processQRCode);
    await Promise.all(promises);

  } catch (err) {

    error.value = t('tables.errorGeneratingZIP');
    loading.value = false;
  }
};

const printQR = () => {
  if (!qrCode.value) return;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const doc = printWindow.document;

  doc.write(`
    <html>
    <head>
      <title>QR kod - ${qrCode.value.table_name}</title>
      <style>
        body { font-family: Arial; text-align: center; padding: 20px; }
        .container { max-width: 400px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 10px; }
        img { max-width: 250px; border: 1px solid #eee; padding: 10px; }
        h1 { color: #D8670C; font-size: 22px; }
        p { color: #666; }
      </style>
    </head>
    <body>
      <div class="container">
        <h1>${qrCode.value.table_name}</h1>
        <img src="${qrCode.value.qr_code_url}" alt="QR kod">
        <p>${qrCode.value.location_name}</p>
        <p style="font-size: 14px; color: #888;">Skeniraj za narudžbu</p>
      </div>
    </body>
    </html>
  `);

  doc.close();

  setTimeout(() => {
    printWindow.print();
  }, 500);
};

const printAllQR = () => {
  if (qrCodes.value.length === 0) return;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const doc = printWindow.document;

  let qrCodesHTML = '';
  qrCodes.value.forEach(qr => {
    qrCodesHTML += `
      <div style="display: inline-block; margin: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid;">
        <h2 style="margin: 0 0 10px 0; font-size: 18px;">${qr.table_name}</h2>
        <img src="${qr.qr_code_url}" alt="QR kod" style="max-width: 200px;">
        <p style="margin: 10px 0 0 0; color: #666;">${qr.location_name}</p>
      </div>
    `;
  });

  doc.write(`
    <html>
    <head>
      <title>QR kodovi</title>
      <style>
        body { font-family: Arial; text-align: center; padding: 20px; }
      </style>
    </head>
    <body>
      ${qrCodesHTML}
    </body>
    </html>
  `);

  doc.close();

  setTimeout(() => {
    printWindow.print();
  }, 500);
};

const tryAgain = () => {
  if (props.singleMode) {
    generateSingleQR();
  } else {
    qrCodes.value = [];
  }
};

onMounted(async () => {
  if (props.singleMode && props.table) {
    generateSingleQR();
  } else {
    await fetchTables();
  }
});
</script>

<style scoped>

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.qr-modal-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.loading-container {
  display: none;

}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  max-width: 350px;
  width: 100%;
}

.error-icon {
  color: #e74c3c;
  font-size: 2rem;
  margin-bottom: 1rem;
}

.qr-floating-card {
  background: linear-gradient(145deg, #f8f9fa 0%, #f0f0f0 100%);
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 340px;
  overflow: hidden;
  position: relative;
  will-change: transform;
  animation: simpleSlide 0.8s ease-out forwards;
  transform: translateX(-100%);
  opacity: 0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.qr-floating-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(145deg,
      rgba(216, 103, 12, 0.05) 0%,
      rgba(216, 103, 12, 0.15) 50%,
      rgba(80, 80, 80, 0.1) 100%);
  z-index: 0;
  pointer-events: none;
}

.qr-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 25px 20px;
  gap: 15px;
  z-index: 1;
  background: linear-gradient(145deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.75) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.qr-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, #505050 0%, #D8670C 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  padding-bottom: 10px;
  position: relative;
}

.qr-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 2px;
  background: linear-gradient(90deg, rgba(216, 103, 12, 0.3) 0%, rgba(216, 103, 12, 0.7) 100%);
  border-radius: 3px;
}

.qr-image-container {
  position: relative;
  margin: 5px 0;
}

.qr-image-container::before {
  content: '';
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  background: linear-gradient(135deg,
      rgba(216, 103, 12, 0.08) 0%,
      rgba(80, 80, 80, 0.05) 100%);
  border-radius: 16px;
  z-index: -1;
  filter: blur(12px);
}

.qr-image {
  max-width: 180px;
  max-height: 180px;
  border: none;
  padding: 15px;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s;
}

.qr-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.location-name {
  margin: 0;
  color: #666;
  font-size: 0.95rem;
  font-weight: 500;
}

.qr-hint {
  background: rgba(240, 240, 240, 0.8);
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 0.85rem;
  color: #777;
  display: inline-block;
  font-weight: 500;
  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.qr-card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 10px;
  width: 100%;
}

.qr-card-actions :deep(.base-btn) {
  width: 100%;
  border-radius: 10px;
  font-size: 0.9rem;
}

.qr-card-actions :deep(.base-btn--primary) {
  box-shadow: 0 8px 20px rgba(216, 103, 12, 0.15);
}

.qr-card-actions :deep(.base-btn--primary):hover {
  box-shadow: 0 10px 25px rgba(216, 103, 12, 0.25);
  transform: translateY(-2px);
}

.qr-card-actions :deep(.base-btn--secondary) {
  background: rgba(245, 245, 245, 0.9);
  color: #555;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.qr-card-actions :deep(.base-btn--secondary):hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  background: rgba(238, 238, 238, 0.95);
}

.floating-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(245, 245, 245, 0.8);
  transition: all 0.3s;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.05);
  color: #666;
  border: none;
  cursor: pointer;
}

.floating-close:hover {
  background: rgba(235, 235, 235, 0.9);
  transform: rotate(90deg) scale(1.1);
  color: #444;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 1rem;
}

.batch-form {
  margin-bottom: 1rem;
}

.radio-group {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.tables-selection {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.5rem;
}

.table-selection-item {
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.selection-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.qr-batch-result {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.qr-batch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.qr-batch-header h3 {
  margin: 0;
}

.batch-actions {
  display: flex;
  gap: 0.5rem;
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.qr-grid-item {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 0.5rem;
  text-align: center;
}

.qr-grid-image {
  max-width: 100%;
  height: auto;
}

.qr-grid-details {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.qr-grid-details h4 {
  margin: 0;
  font-size: 1rem;
}

.qr-grid-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: #f3f4f6;
  color: #2563eb;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.btn-icon:hover {
  background-color: #e5e7eb;
  color: #1d4ed8;
}

.no-tables {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
}

@keyframes simpleSlide {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }

  70% {
    transform: translateX(10%);
    opacity: 1;
  }

  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 380px) {
  .qr-floating-card {
    max-width: 300px;
  }

  .floating-close {
    top: 10px;
    right: 10px;
    width: 28px;
    height: 28px;
  }
}

.qr-batch-modal {
  background: linear-gradient(145deg, #f8f9fa 0%, #f0f0f0 100%);
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  animation: fadeIn 0.4s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.qr-batch-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(145deg,
      rgba(216, 103, 12, 0.05) 0%,
      rgba(216, 103, 12, 0.1) 50%,
      rgba(80, 80, 80, 0.05) 100%);
  border-radius: 20px;
  z-index: 0;
  pointer-events: none;
}

.qr-batch-content {
  position: relative;
  padding: 30px;
  z-index: 1;
}

.batch-title {
  margin: 0 0 20px 0;
  font-size: 1.6rem;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(135deg, #505050 0%, #D8670C 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  padding-bottom: 10px;
}

.batch-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, rgba(216, 103, 12, 0.3) 0%, rgba(216, 103, 12, 0.7) 100%);
  border-radius: 3px;
}

.batch-option-container {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
}

.batch-option-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: #f0f0f0;
  border-radius: 12px;
  padding: 5px;
}

.batch-option-tab {
  flex: 1;
  padding: 12px 20px;
  text-align: center;
  cursor: pointer;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.batch-option-tab.active {
  background: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  color: #D8670C;
}

.tab-icon {
  font-size: 0.9rem;
}

.selection-title {
  font-size: 1.1rem;
  margin: 0 0 15px 0;
  color: #333;
  font-weight: 500;
}

.selection-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.select-action {
  background: none;
  border: none;
  font-size: 0.85rem;
  color: #2563eb;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
}

.select-action:hover {
  background: rgba(37, 99, 235, 0.1);
}

.tables-selection-container {
  margin-top: 15px;
}

.tables-selection {
  max-height: 300px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  padding: 10px;
  margin-top: 10px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.05);
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.table-selection-item {
  padding: 10px 15px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.table-selection-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.08);
}

.table-selection-item.selected {
  background: rgba(216, 103, 12, 0.08);
  border-color: rgba(216, 103, 12, 0.2);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  width: 100%;
}

.checkbox-input {
  accent-color: #D8670C;
  width: 16px;
  height: 16px;
}

.table-name {
  font-weight: 500;
  color: #444;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.no-tables {
  padding: 25px;
  text-align: center;
  color: #777;
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.no-tables-icon {
  font-size: 2rem;
  color: #ccc;
}

.qr-batch-result {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.qr-batch-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 15px 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.result-title {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-icon {
  color: #10b981;
}

.batch-actions {
  display: flex;
  gap: 10px;
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.qr-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  position: relative;
}

.qr-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.qr-card-inner {
  display: flex;
  flex-direction: column;
}

.qr-card-header {
  padding: 12px 15px;
  background: linear-gradient(145deg, #f8f9fa 0%, #f0f0f0 100%);
  border-bottom: 1px solid #eee;
}

.qr-card-header h4 {
  margin: 0;
  font-size: 1rem;
  color: #333;
  text-align: center;
  font-weight: 600;
}

.qr-card-image {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-card-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.qr-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: rgba(245, 245, 245, 0.5);
  border-top: 1px solid #eee;
}

.qr-location {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
}

.btn-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(216, 103, 12, 0.1);
  color: #D8670C;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-download:hover {
  background: rgba(216, 103, 12, 0.2);
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .qr-batch-content {
    padding: 20px;
  }

  .qr-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  .batch-title {
    font-size: 1.4rem;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}

@media (max-width: 480px) {
  .qr-batch-content {
    padding: 15px;
  }

  .qr-batch-header {
    flex-direction: column;
    gap: 15px;
  }

  .batch-actions {
    width: 100%;
  }

  .batch-actions :deep(.base-btn) {
    flex: 1;
  }

  .tables-grid {
    grid-template-columns: 1fr;
  }

  .batch-option-tab {
    padding: 10px;
    font-size: 0.85rem;
  }
}

@media (max-width: 420px) {
  .qr-card-content {
    padding: 20px 15px;
    gap: 12px;
  }

  .qr-image {
    max-width: 160px;
    max-height: 160px;
    padding: 12px;
  }

  .qr-title {
    font-size: 1.3rem;
    padding-bottom: 8px;
  }

  .qr-card-actions :deep(.base-btn) {
    padding: 0.7rem 1rem;
  }

  .floating-close {
    top: 10px;
    right: 10px;
    width: 28px;
    height: 28px;
  }
}
</style>